import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  accountHue,
  api,
  type AddressInput,
  type MessageView,
  type PublicAccount,
  type SendPayload,
  type OutgoingAttachment,
  MAX_ATTACHMENT_BYTES,
  type ThreadView,
} from '../lib/api';
import { isMac } from '../lib/keymap';
import { useUiStore, type ComposerDraft, type ComposerState } from '../lib/store';
import { useKeyboard } from '../hooks/useKeyboard';
import { ToastOutlet, useToast } from './Toast';
import { formatBytes } from '../lib/format';
import styles from './Composer.module.css';

/** Outbound addresses are the shared client's shape; do not fork it. */
export type Recipient = AddressInput;

export type ComposerMode = 'new' | 'reply' | 'reply-all' | 'forward';

export type ComposerIntent = {
  mode: ComposerMode;
  popout?: boolean;
  threadKey?: string | null;
  /** Message being answered; defaults to the newest non-draft in the thread. */
  parentId?: number | null;
  /** Overrides identity resolution (used when restoring an undone send). */
  accountId?: number | null;
  to?: Recipient[];
  cc?: Recipient[];
  bcc?: Recipient[];
  subject?: string;
  bodyHtml?: string;
  /** Provided when restoring a draft; otherwise derived from the parent. */
  inReplyTo?: string | null;
  references?: string | null;
};

export type SentInfo = { threadKey: string | null; accountId: number; messageId: string };

export type ComposerProps = {
  accounts: PublicAccount[];
  aiEnabled: boolean;
  /** null renders the 44px collapsed "Reply" affordance. */
  intent: ComposerIntent | null;
  /** Thread open in the reading pane — context for the collapsed state and replies. */
  thread?: ThreadView | null;
  /** That thread's messages, oldest first. */
  messages?: MessageView[];
  lastUsedAccountId?: number | null;
  onOpen: (intent: ComposerIntent) => void;
  onClose: () => void;
  onSent?: (info: SentInfo) => void;
  onAccountUsed?: (accountId: number) => void;
};

type DraftState = {
  key: string;
  mode: ComposerMode;
  accountId: number;
  to: Recipient[];
  cc: Recipient[];
  bcc: Recipient[];
  subject: string;
  bodyHtml: string;
  inReplyTo: string | null;
  references: string | null;
  threadKey: string | null;
  showCc: boolean;
};

const UNDO_WINDOW_MS = 10_000;
const SCHEDULED_TOAST_MS = 8_000;
const AUTOSAVE_MS = 400;
const DRAFT_PREFIX = 'kiln.draft.';
const PENDING_KEY = 'kiln.pending-sends';

/* --------------------------- identity resolution -------------------------- */

function ownAddresses(accounts: PublicAccount[]): Set<string> {
  const set = new Set<string>();
  for (const account of accounts) {
    set.add(account.email.toLowerCase());
    for (const alias of (account.sendAs ?? '').split(/[,;\s]+/)) {
      if (alias.includes('@')) set.add(alias.toLowerCase());
    }
  }
  return set;
}

function accountAddresses(account: PublicAccount): string[] {
  const list = [account.email.toLowerCase()];
  for (const alias of (account.sendAs ?? '').split(/[,;\s]+/)) {
    if (alias.includes('@')) list.push(alias.toLowerCase());
  }
  return list;
}

/**
 * The multi-account payoff: replying from the address the mail was addressed
 * to, not from whichever account happens to be first.
 */
export function resolveFromAccount(
  accounts: PublicAccount[],
  parent: MessageView | null,
  thread: ThreadView | null,
  lastUsedAccountId: number | null,
): PublicAccount | null {
  const usable = accounts.filter((a) => a.enabled);
  if (usable.length === 0) return null;

  if (parent) {
    const recipients = new Set(
      [...parent.to, ...parent.cc].map((person) => person.address.toLowerCase()),
    );
    const addressed = usable.find((account) =>
      accountAddresses(account).some((address) => recipients.has(address)),
    );
    if (addressed) return addressed;

    const receiving = usable.find((account) => account.id === parent.accountId);
    if (receiving) return receiving;
  }

  if (thread) {
    const receiving = usable.find((account) => thread.accountIds.includes(account.id));
    if (receiving) return receiving;
  }

  const lastUsed = usable.find((account) => account.id === lastUsedAccountId);
  return lastUsed ?? usable[0] ?? null;
}

export function buildReplyRecipients(
  parent: MessageView,
  all: boolean,
  accounts: PublicAccount[],
): { to: Recipient[]; cc: Recipient[] } {
  const mine = ownAddresses(accounts);
  const seen = new Set<string>();
  const take = (people: { name: string; address: string }[]): Recipient[] => {
    const out: Recipient[] = [];
    for (const person of people) {
      const address = person.address.toLowerCase();
      if (address === '' || mine.has(address) || seen.has(address)) continue;
      seen.add(address);
      out.push({ name: person.name || undefined, address: person.address });
    }
    return out;
  };

  const to = take([parent.from]);
  if (!all) return { to, cc: [] };
  const rest = take([...parent.to, ...parent.cc]);
  return { to: to.length > 0 ? to : rest.slice(0, 1), cc: to.length > 0 ? rest : rest.slice(1) };
}

/**
 * MessageView does not carry the raw References header, so the chain is
 * reconstructed from the thread itself — every message id up to and including
 * the parent, which is what a conforming client would have produced anyway.
 */
function buildReferences(messages: MessageView[], parent: MessageView): string | null {
  const chain: string[] = [];
  for (const message of messages) {
    if (message.messageId) chain.push(message.messageId);
    if (message.id === parent.id) break;
  }
  return chain.length > 0 ? chain.join(' ') : null;
}

/* -------------------------------- utilities ------------------------------- */

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatPerson(person: { name?: string; address: string }): string {
  return person.name && person.name !== person.address ? `${person.name} <${person.address}>` : person.address;
}

export function parseRecipient(input: string): Recipient | null {
  const text = input.trim().replace(/[,;]+$/, '');
  if (text === '') return null;
  const angled = /^(.*?)<([^>]+)>$/.exec(text);
  if (angled) {
    const address = (angled[2] ?? '').trim();
    if (!address.includes('@')) return null;
    const name = (angled[1] ?? '').trim().replace(/^"|"$/g, '');
    return name === '' ? { address } : { name, address };
  }
  if (!text.includes('@') || /\s/.test(text)) return null;
  return { address: text };
}

function quoteHeader(parent: MessageView): string {
  const when = new Date(parent.date * 1000).toLocaleString();
  return `On ${when}, ${formatPerson(parent.from)} wrote:`;
}

function buildQuotedBody(parent: MessageView, forward: boolean): string {
  const body = parent.bodyHtml ?? `<p>${escapeHtml(parent.bodyText ?? parent.snippet)}</p>`;
  if (forward) {
    const header = [
      `From: ${escapeHtml(formatPerson(parent.from))}`,
      `Date: ${escapeHtml(new Date(parent.date * 1000).toLocaleString())}`,
      `Subject: ${escapeHtml(parent.subject)}`,
      `To: ${escapeHtml(parent.to.map(formatPerson).join(', '))}`,
    ].join('<br>');
    return `<p><br></p><div data-kiln-quote="1"><p>---------- Forwarded message ----------</p><p>${header}</p>${body}</div>`;
  }
  return `<p><br></p><div data-kiln-quote="1"><p>${escapeHtml(quoteHeader(parent))}</p><blockquote>${body}</blockquote></div>`;
}

function replySubject(subject: string): string {
  return /^re:/i.test(subject.trim()) ? subject : `Re: ${subject}`;
}

function forwardSubject(subject: string): string {
  return /^fwd?:/i.test(subject.trim()) ? subject : `Fwd: ${subject}`;
}

/* ------------------------- pending sends (module) ------------------------- */

type PendingSend = {
  id: string;
  payload: SendPayload;
  archiveAfter: boolean;
  sendAt: number;
  intent: ComposerIntent;
};

type PendingRecord = PendingSend & { timer: ReturnType<typeof setTimeout> | null };

const pendingSends = new Map<string, PendingRecord>();
const pendingListeners = new Set<() => void>();
let pendingSeq = 0;
let resumed = false;

function notifyPending(): void {
  for (const listener of pendingListeners) listener();
}

export function hasPendingSend(): boolean {
  return pendingSends.size > 0;
}

export function subscribePendingSend(listener: () => void): () => void {
  pendingListeners.add(listener);
  return () => {
    pendingListeners.delete(listener);
  };
}

function persistPending(): void {
  if (typeof localStorage === 'undefined') return;
  const entries = [...pendingSends.values()].map(({ id, payload, archiveAfter, sendAt, intent }) => ({
    id,
    payload,
    archiveAfter,
    sendAt,
    intent,
  }));
  try {
    if (entries.length === 0) localStorage.removeItem(PENDING_KEY);
    else localStorage.setItem(PENDING_KEY, JSON.stringify(entries));
  } catch {
    // Storage failure must not stop mail from going out.
  }
}

async function deliver(entry: PendingRecord): Promise<{ messageId: string }> {
  // Removed from the queue before the request goes out: a retry would post twice.
  pendingSends.delete(entry.id);
  persistPending();
  notifyPending();
  const result = await api.send(entry.payload);
  if (entry.archiveAfter && entry.payload.threadKey) {
    await api.threadAction(entry.payload.threadKey, 'archive');
  }
  return { messageId: result.messageId };
}

function armTimer(
  entry: PendingRecord,
  onDone: (entry: PendingRecord, messageId: string) => void,
  onFail: (entry: PendingRecord, error: unknown) => void,
): void {
  const delay = Math.max(0, entry.sendAt - Date.now());
  entry.timer = setTimeout(() => {
    void deliver(entry).then(
      (result) => onDone(entry, result.messageId),
      (error: unknown) => onFail(entry, error),
    );
  }, delay);
}

function cancelPendingSend(id: string): PendingRecord | null {
  const entry = pendingSends.get(id);
  if (!entry) return null;
  if (entry.timer !== null) clearTimeout(entry.timer);
  pendingSends.delete(id);
  persistPending();
  notifyPending();
  return entry;
}

/** Fire every queued send right now — the ⌘⇧Z "send immediately" path. */
export function flushPendingSends(): void {
  for (const entry of [...pendingSends.values()]) {
    if (entry.timer !== null) clearTimeout(entry.timer);
    entry.sendAt = Date.now();
    void deliver(entry);
  }
}

/**
 * Cancels the most recent queued send and hands its draft back, so the global
 * `z` undo can reopen a message that has not gone out yet.
 */
export function undoLastPendingSend(): ComposerIntent | null {
  const latest = [...pendingSends.values()].sort((a, b) => b.sendAt - a.sendAt)[0];
  if (!latest) return null;
  cancelPendingSend(latest.id);
  return latest.intent;
}

function isPendingSend(value: unknown): value is PendingSend {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Partial<PendingSend>;
  return (
    typeof record.id === 'string' &&
    typeof record.sendAt === 'number' &&
    typeof record.payload === 'object' &&
    record.payload !== null
  );
}

/**
 * A queued send must survive a reload, so entries are persisted and re-armed
 * once. The entry is removed from storage before delivery, which is what stops
 * a second tab from sending the same message twice.
 */
function resumePendingSends(): void {
  if (resumed || typeof localStorage === 'undefined') return;
  resumed = true;
  let stored: unknown;
  try {
    stored = JSON.parse(localStorage.getItem(PENDING_KEY) ?? '[]');
  } catch {
    return;
  }
  if (!Array.isArray(stored)) return;
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    return;
  }
  for (const raw of stored) {
    if (!isPendingSend(raw)) continue;
    const entry: PendingRecord = { ...raw, timer: null };
    pendingSends.set(entry.id, entry);
    armTimer(
      entry,
      () => notifyPending(),
      () => notifyPending(),
    );
  }
  persistPending();
  notifyPending();
}

/* --------------------------------- drafts --------------------------------- */

function draftStorageKey(mode: ComposerMode, threadKey: string | null): string {
  return `${DRAFT_PREFIX}${mode}:${threadKey ?? 'new'}`;
}

type StoredDraft = Pick<DraftState, 'accountId' | 'to' | 'cc' | 'bcc' | 'subject' | 'bodyHtml'>;

function isStoredDraft(value: unknown): value is StoredDraft {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Partial<StoredDraft>;
  return typeof record.subject === 'string' && typeof record.bodyHtml === 'string' && Array.isArray(record.to);
}

function loadDraft(key: string): StoredDraft | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
    return isStoredDraft(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/* ------------------------------- the component ---------------------------- */

function ComposerView({
  accounts,
  aiEnabled,
  intent,
  thread = null,
  messages = [],
  lastUsedAccountId = null,
  onOpen,
  onClose,
  onSent,
  onAccountUsed,
}: ComposerProps): ReactElement | null {
  const toasts = useToast();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [popout, setPopout] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiDrafted, setAiDrafted] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [attachments, setAttachments] = useState<OutgoingAttachment[]>([]);
  const [attaching, setAttaching] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [toInput, setToInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [laterOpen, setLaterOpen] = useState(false);
  const [selectionToolbar, setSelectionToolbar] = useState<{ top: number; left: number } | null>(null);
  const [bodyRevision, setBodyRevision] = useState(0);

  const editorRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const initialBodyRef = useRef('');
  const bodyHtmlRef = useRef('');
  const aiChipRetired = useRef(false);
  const subjectWarned = useRef(false);
  const dragState = useRef<{ pointerId: number; startX: number; startY: number; baseX: number; baseY: number } | null>(
    null,
  );

  useEffect(resumePendingSends, []);

  const dock = useDockTarget(!popout);

  const enabledAccounts = useMemo(() => accounts.filter((account) => account.enabled), [accounts]);

  const parent = useMemo<MessageView | null>(() => {
    if (messages.length === 0) return null;
    if (intent?.parentId != null) {
      const found = messages.find((message) => message.id === intent.parentId);
      if (found) return found;
    }
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message && !message.draft) return message;
    }
    return messages[messages.length - 1] ?? null;
  }, [messages, intent?.parentId]);

  /* Build the draft whenever a new intent arrives. */
  // Deliberately coarse: it must identify a *new* draft, not every re-render of
  // the thread, or a live message arriving would erase what the user typed.
  const intentSignature = intent
    ? `${intent.mode}|${intent.threadKey ?? thread?.threadKey ?? ''}|${intent.parentId ?? ''}|${
        messages.length > 0 ? 'ready' : 'empty'
      }`
    : '';

  useEffect(() => {
    if (!intent) {
      setDraft(null);
      return;
    }

    const threadKey = intent.threadKey ?? thread?.threadKey ?? null;
    const isReply = intent.mode === 'reply' || intent.mode === 'reply-all';
    const account =
      (intent.accountId != null ? enabledAccounts.find((a) => a.id === intent.accountId) : undefined) ??
      resolveFromAccount(accounts, isReply || intent.mode === 'forward' ? parent : null, thread, lastUsedAccountId);

    if (!account) {
      setError('Add an account before writing mail.');
      return;
    }

    let to: Recipient[] = intent.to ?? [];
    let cc: Recipient[] = intent.cc ?? [];
    if (isReply && parent && !intent.to) {
      const built = buildReplyRecipients(parent, intent.mode === 'reply-all', accounts);
      to = built.to;
      cc = built.cc;
    }

    const subject =
      intent.subject ??
      (isReply && parent
        ? replySubject(parent.subject || thread?.subject || '')
        : intent.mode === 'forward' && parent
          ? forwardSubject(parent.subject || thread?.subject || '')
          : '');

    const quoted =
      intent.bodyHtml ??
      (parent && (isReply || intent.mode === 'forward') ? buildQuotedBody(parent, intent.mode === 'forward') : '<p><br></p>');

    const key = draftStorageKey(intent.mode, threadKey);
    const stored = intent.bodyHtml === undefined ? loadDraft(key) : null;

    const next: DraftState = {
      key,
      mode: intent.mode,
      accountId: stored?.accountId ?? account.id,
      to: stored?.to ?? to,
      cc: stored?.cc ?? cc,
      bcc: stored?.bcc ?? intent.bcc ?? [],
      subject: stored?.subject ?? subject,
      bodyHtml: stored?.bodyHtml ?? quoted,
      inReplyTo: intent.inReplyTo ?? (isReply && parent ? parent.messageId : null),
      references: intent.references ?? (isReply && parent ? buildReferences(messages, parent) : null),
      threadKey,
      showCc: (stored?.cc ?? cc).length > 0 || (stored?.bcc ?? []).length > 0,
    };

    initialBodyRef.current = next.bodyHtml;
    bodyHtmlRef.current = next.bodyHtml;
    aiChipRetired.current = false;
    subjectWarned.current = false;
    setDraft(next);
    setPopout(intent.popout === true);
    setMinimized(false);
    setAiDrafted(false);
    setAiOpen(false);
    setError(null);
    setToInput('');
    setCcInput('');
    setSavedAt(null);
    setBodyRevision((revision) => revision + 1);
    // Recomputing on every message re-render would wipe what the user typed;
    // the intent signature is what actually identifies a new draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intentSignature]);

  const open = draft !== null && intent !== null;
  const fromAccount = enabledAccounts.find((account) => account.id === draft?.accountId) ?? null;
  const fromHue = fromAccount ? accountHue(fromAccount.id, accounts) : 'var(--accent-9)';

  /* --------------------------------- autosave ------------------------------ */

  const draftRef = useRef<DraftState | null>(null);
  draftRef.current = draft;
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleSave = useCallback(() => {
    if (saveTimer.current !== null) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const current = draftRef.current;
      if (!current) return;
      const payload: StoredDraft = {
        accountId: current.accountId,
        to: current.to,
        cc: current.cc,
        bcc: current.bcc,
        subject: current.subject,
        bodyHtml: bodyHtmlRef.current,
      };
      try {
        localStorage.setItem(current.key, JSON.stringify(payload));
        setSavedAt(Date.now());
      } catch {
        // Nothing to do: the draft still lives in memory.
      }
    }, AUTOSAVE_MS);
  }, []);

  useEffect(() => {
    if (draft) scheduleSave();
  }, [draft, scheduleSave]);

  useEffect(
    () => () => {
      if (saveTimer.current !== null) clearTimeout(saveTimer.current);
    },
    [],
  );

  const patch = useCallback((changes: Partial<DraftState>) => {
    setDraft((current) => (current ? { ...current, ...changes } : current));
  }, []);

  // Typing must not re-render the composer on every keystroke, so the body
  // lives in a ref and only the debounced save touches React state.
  const readEditor = useCallback(() => {
    const node = editorRef.current;
    if (!node) return;
    bodyHtmlRef.current = node.innerHTML;
    scheduleSave();
  }, [scheduleSave]);

  /* ---------------------------------- send -------------------------------- */


  /**
   * Read files into base64 for the send payload.
   *
   * Inline rather than a separate upload endpoint: the whole message has to fit
   * one SMTP transaction anyway, so staging uploads server-side would add a
   * lifecycle (orphaned blobs, cleanup, resumability) for no gain at these
   * sizes. FileReader is used over arrayBuffer+btoa because btoa on a large
   * Uint8Array via spread blows the call stack somewhere north of a megabyte.
   */
  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      setAttaching(true);
      setError(null);
      try {
        const read = await Promise.all(
          list.map(
            (file) =>
              new Promise<OutgoingAttachment>((resolve, reject) => {
                const reader = new FileReader();
                reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
                reader.onload = () => {
                  const result = String(reader.result);
                  // Strip the "data:<mime>;base64," prefix.
                  const comma = result.indexOf(',');
                  resolve({
                    filename: file.name,
                    contentType: file.type || 'application/octet-stream',
                    content: comma === -1 ? result : result.slice(comma + 1),
                    size: file.size,
                  });
                };
                reader.readAsDataURL(file);
              }),
          ),
        );

        setAttachments((prev) => {
          const next = [...prev, ...read];
          const total = next.reduce((sum, a) => sum + a.size, 0);
          if (total > MAX_ATTACHMENT_BYTES) {
            setError(
              `Attachments would total ${(total / 1024 / 1024).toFixed(1)} MB. The limit is ` +
                `${MAX_ATTACHMENT_BYTES / 1024 / 1024} MB — most providers reject a message above 25 MB once encoded.`,
            );
            return prev;
          }
          return next;
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not read those files.');
      } finally {
        setAttaching(false);
        // Clear the input so picking the same file twice still fires onChange.
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [setError],
  );

  const removeAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const attachedBytes = attachments.reduce((sum, a) => sum + a.size, 0);

  const discard = useCallback(() => {
    if (draft) {
      try {
        localStorage.removeItem(draft.key);
      } catch {
        // Ignore: a stale draft is harmless.
      }
    }
    setDraft(null);
    onClose();
  }, [draft, onClose]);

  const queueSend = useCallback(
    (options: { archiveAfter?: boolean; delayMs?: number; label?: string }) => {
      const node = editorRef.current;
      const current = draft;
      if (!current) return;
      const html = node?.innerHTML ?? bodyHtmlRef.current;
      const text = node?.innerText ?? '';
      const recipients = [...current.to];
      const trailing = parseRecipient(toInput);
      if (trailing) recipients.push(trailing);

      if (recipients.length === 0) {
        setError('Add at least one recipient.');
        return;
      }
      if (current.subject.trim() === '' && !subjectWarned.current) {
        // A modal dialog would break the keyboard flow; a second Send confirms.
        subjectWarned.current = true;
        setError('No subject — press Send again to send anyway.');
        return;
      }

      const payload: SendPayload = {
        accountId: current.accountId,
        to: recipients,
        cc: current.cc.length > 0 ? current.cc : undefined,
        bcc: current.bcc.length > 0 ? current.bcc : undefined,
        subject: current.subject,
        text,
        html,
        inReplyTo: current.inReplyTo,
        references: current.references,
        threadKey: current.threadKey,
        attachments:
          attachments.length > 0
            ? attachments.map(({ filename, contentType, content }) => ({ filename, contentType, content }))
            : undefined,
      };

      const restore: ComposerIntent = {
        mode: current.mode,
        popout,
        threadKey: current.threadKey,
        accountId: current.accountId,
        to: recipients,
        cc: current.cc,
        bcc: current.bcc,
        subject: current.subject,
        bodyHtml: html,
        inReplyTo: current.inReplyTo,
        references: current.references,
      };

      const delay = options.delayMs ?? UNDO_WINDOW_MS;
      const entry: PendingRecord = {
        id: `send-${pendingSeq++}-${Date.now()}`,
        payload,
        archiveAfter: options.archiveAfter === true,
        sendAt: Date.now() + delay,
        intent: restore,
        timer: null,
      };
      pendingSends.set(entry.id, entry);
      persistPending();
      notifyPending();

      const invalidate = () => {
        void queryClient.invalidateQueries({ queryKey: ['threads'] });
        if (current.threadKey) void queryClient.invalidateQueries({ queryKey: ['thread', current.threadKey] });
      };

      armTimer(
        entry,
        (_entry, messageId) => {
          invalidate();
          onSent?.({ threadKey: current.threadKey, accountId: current.accountId, messageId });
        },
        (_failed, failure) => {
          const detail = failure instanceof Error ? failure.message : 'Could not reach the server';
          toasts.toast({
            title: 'Message not sent',
            description: detail,
            tone: 'danger',
            duration: 0,
            action: { label: 'Reopen', onClick: () => onOpen(restore) },
          });
        },
      );

      onAccountUsed?.(current.accountId);
      try {
        localStorage.removeItem(current.key);
      } catch {
        // Ignore.
      }
      setDraft(null);
      onClose();

      const scheduled = options.delayMs !== undefined;
      toasts.toast({
        title: options.label ?? 'Sending…',
        description: scheduled ? 'Undo from the queue until it goes out' : formatPerson(recipients[0] ?? { address: '' }),
        countdown: !scheduled,
        duration: scheduled ? SCHEDULED_TOAST_MS : delay,
        action: {
          label: 'Undo',
          keys: 'Z',
          onClick: () => {
            const cancelled = cancelPendingSend(entry.id);
            if (cancelled) onOpen(cancelled.intent);
          },
        },
      });
    },
    [draft, toInput, popout, queryClient, toasts, onClose, onOpen, onSent, onAccountUsed],
  );

  /* --------------------------------- AI ----------------------------------- */

  const retireAiChip = useCallback(() => {
    if (aiChipRetired.current) return;
    aiChipRetired.current = true;
    setAiDrafted(false);
  }, []);

  const runAiDraft = useCallback(async () => {
    const threadKey = draft?.threadKey;
    if (!threadKey) return;
    setAiBusy(true);
    try {
      const result = await api.ai.reply(threadKey, instruction.trim() === '' ? undefined : instruction.trim());
      const html = result.draft
        .split(/\n{2,}/)
        .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
        .join('');
      initialBodyRef.current = `${html}${bodyHtmlRef.current}`;
      bodyHtmlRef.current = initialBodyRef.current;
      patch({ bodyHtml: initialBodyRef.current });
      setBodyRevision((revision) => revision + 1);
      aiChipRetired.current = false;
      setAiDrafted(true);
      setAiOpen(false);
      setInstruction('');
    } catch {
      setError('The assistant could not draft that.');
    } finally {
      setAiBusy(false);
    }
  }, [draft?.threadKey, instruction, patch]);

  const transformSelection = useCallback(
    async (label: string, directive: string) => {
      const threadKey = draft?.threadKey;
      const selection = window.getSelection();
      if (!threadKey || !selection || selection.rangeCount === 0 || selection.isCollapsed) return;
      const range = selection.getRangeAt(0);
      const passage = selection.toString();
      if (passage.trim() === '') return;
      setAiBusy(true);
      try {
        // /api/ai/reply is the only text endpoint in the contract; the directive
        // pins it to rewriting the marked passage rather than answering the mail.
        const result = await api.ai.reply(
          threadKey,
          `${directive}\nReturn only the rewritten passage, with no preamble.\nPassage:\n<<<${passage}>>>`,
        );
        range.deleteContents();
        range.insertNode(document.createTextNode(result.draft.trim()));
        selection.removeAllRanges();
        readEditor();
        aiChipRetired.current = false;
        setAiDrafted(true);
        setSelectionToolbar(null);
      } catch {
        setError(`${label} failed.`);
      } finally {
        setAiBusy(false);
      }
    },
    [draft?.threadKey, readEditor],
  );

  useEffect(() => {
    if (!aiEnabled || !open) return;
    const onSelectionChange = () => {
      const editor = editorRef.current;
      const root = rootRef.current;
      const selection = window.getSelection();
      if (!editor || !root || !selection || selection.rangeCount === 0 || selection.isCollapsed) {
        setSelectionToolbar(null);
        return;
      }
      const range = selection.getRangeAt(0);
      if (!editor.contains(range.commonAncestorContainer)) {
        setSelectionToolbar(null);
        return;
      }
      const rect = range.getBoundingClientRect();
      const bounds = root.getBoundingClientRect();
      setSelectionToolbar({ top: rect.top - bounds.top - 38, left: rect.left - bounds.left });
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [aiEnabled, open]);

  /* ------------------------------- keyboard ------------------------------- */

  useKeyboard(
    'composer',
    {
      send: () => queueSend({}),
      sendAndArchive: () => queueSend({ archiveAfter: true }),
      sendLater: () => setLaterOpen((value) => !value),
      discardDraft: discard,
      openAiInstruction: () => {
        if (!aiEnabled || !draft?.threadKey) return;
        setAiOpen(true);
      },
      goBack: () => {
        setDraft(null);
        onClose();
      },
    },
    { enabled: open && !minimized },
  );

  /* --------------------------------- drag --------------------------------- */

  const onDragStart = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!popout || event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragState.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      baseX: offset.x,
      baseY: offset.y,
    };
  };

  const onDragMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const state = dragState.current;
    if (!state || state.pointerId !== event.pointerId) return;
    const nextX = state.baseX + (event.clientX - state.startX);
    const nextY = state.baseY + (event.clientY - state.startY);
    setOffset({
      x: Math.min(0, Math.max(-(window.innerWidth - 260), nextX)),
      y: Math.min(0, Math.max(-(window.innerHeight - 120), nextY)),
    });
  };

  const onDragEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    const state = dragState.current;
    if (!state) return;
    dragState.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
    // Snap back to the bottom-right dock when released near it.
    if (Math.abs(offset.x) < 60 && Math.abs(offset.y) < 60) setOffset({ x: 0, y: 0 });
  };

  /* -------------------------------- render -------------------------------- */

  if (!open || !draft || !fromAccount) {
    if (!thread || dock === null) return null;
    return createPortal(
      <div className={styles.collapsed}>
        <button
          type="button"
          className={styles.collapsedButton}
          onClick={() => onOpen({ mode: 'reply', threadKey: thread.threadKey })}
        >
          <span>Reply</span>
          <kbd className={styles.kbd}>R</kbd>
        </button>
        <button
          type="button"
          className={styles.collapsedGhost}
          onClick={() => onOpen({ mode: 'reply-all', threadKey: thread.threadKey })}
        >
          Reply all
          <kbd className={styles.kbd}>A</kbd>
        </button>
        <button
          type="button"
          className={styles.collapsedGhost}
          onClick={() => onOpen({ mode: 'forward', threadKey: thread.threadKey })}
        >
          Forward
          <kbd className={styles.kbd}>F</kbd>
        </button>
        {error ? <span className={styles.error}>{error}</span> : null}
      </div>,
      dock,
    );
  }

  if (minimized) {
    const tab = (
      <button type="button" className={styles.minimizedTab} onClick={() => setMinimized(false)}>
        <span className={styles.tabRail} style={{ background: fromHue }} aria-hidden="true" />
        <span className={styles.tabLabel}>{draft.subject || 'New message'}</span>
      </button>
    );
    return createPortal(tab, document.body);
  }

  const floating = !popout && dock === null;
  const rootClass = [styles.composer, popout ? styles.popout : '', floating ? styles.floating : '']
    .filter(Boolean)
    .join(' ');

  const body = (
    <div
      ref={rootRef}
      className={rootClass}
      style={
        popout
          ? railStyle(fromHue, `translate3d(${offset.x}px, ${offset.y}px, 0)`)
          : railStyle(fromHue)
      }
      role="region"
      aria-label={draft.mode === 'new' ? 'New message' : `${draft.mode} to ${draft.to.map((r) => r.address).join(', ')}`}
      data-dragover={dragOver}
      onDragOver={(event) => {
        // Only claim the drag when it actually carries files; a text selection
        // dragged within the editor must keep its normal behaviour.
        if (!event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={(event) => {
        // relatedTarget is null when the pointer leaves the window entirely, and
        // outside the root when it moves to a sibling — both end the drag.
        const next = event.relatedTarget as Node | null;
        if (!next || !event.currentTarget.contains(next)) setDragOver(false);
      }}
      onDrop={(event) => {
        if (!event.dataTransfer.types.includes('Files')) return;
        event.preventDefault();
        setDragOver(false);
        if (event.dataTransfer.files.length > 0) void addFiles(event.dataTransfer.files);
      }}
    >
      <span className={styles.rail} aria-hidden="true" />

      {popout ? (
        <div
          className={styles.titlebar}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
        >
          <span className={styles.titleText}>{draft.subject || 'New message'}</span>
          <div className={styles.titleActions}>
            <button type="button" className={styles.iconButton} aria-label="Minimize" onClick={() => setMinimized(true)}>
              <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
                <path d="M2 9h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
            <button type="button" className={styles.iconButton} aria-label="Dock" onClick={() => setPopout(false)}>
              <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
                <rect x="1.5" y="1.5" width="9" height="9" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
              </svg>
            </button>
            <button type="button" className={styles.iconButton} aria-label="Discard" onClick={discard}>
              <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
                <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      ) : null}

      <div className={styles.addressBar}>
        <label className={styles.fromSelect}>
          <span className={styles.fromDot} style={{ background: fromHue }} aria-hidden="true" />
          <select
            className={styles.select}
            value={draft.accountId}
            aria-label="Send from"
            onChange={(event) => patch({ accountId: Number(event.target.value) })}
          >
            {enabledAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.displayName ? `${account.displayName} · ${account.email}` : account.email}
              </option>
            ))}
          </select>
        </label>

        <div className={styles.recipients}>
          {draft.to.map((recipient, index) => (
            <span key={`${recipient.address}-${index}`} className={styles.chip}>
              {recipient.name ?? recipient.address}
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Remove ${recipient.address}`}
                onClick={() => patch({ to: draft.to.filter((_, i) => i !== index) })}
              >
                <svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true">
                  <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
            </span>
          ))}
          <input
            className={styles.recipientInput}
            value={toInput}
            aria-label="To"
            placeholder={draft.to.length === 0 ? 'To' : ''}
            autoComplete="off"
            onChange={(event) => setToInput(event.target.value)}
            onBlur={() => {
              const parsed = parseRecipient(toInput);
              if (parsed) {
                patch({ to: [...draft.to, parsed] });
                setToInput('');
              }
            }}
            onKeyDown={(event: ReactKeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter' || event.key === ',' || event.key === 'Tab') {
                const parsed = parseRecipient(toInput);
                if (parsed) {
                  event.preventDefault();
                  patch({ to: [...draft.to, parsed] });
                  setToInput('');
                }
                return;
              }
              if (event.key === 'Backspace' && toInput === '' && draft.to.length > 0) {
                event.preventDefault();
                patch({ to: draft.to.slice(0, -1) });
              }
            }}
          />
        </div>

        <button
          type="button"
          className={styles.ccToggle}
          aria-pressed={draft.showCc}
          onClick={() => patch({ showCc: !draft.showCc })}
        >
          Cc
        </button>
        {!popout ? (
          <button
            type="button"
            className={styles.iconButton}
            aria-label="Open in a window"
            onClick={() => setPopout(true)}
          >
            <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
              <path d="M4.5 1.5H10.5V7.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M10.5 1.5L5 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path
                d="M8.5 10.5H1.5V3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : null}
      </div>

      {draft.showCc ? (
        <div className={styles.ccBar}>
          <span className={styles.fieldLabel}>Cc</span>
          <div className={styles.recipients}>
            {draft.cc.map((recipient, index) => (
              <span key={`${recipient.address}-${index}`} className={styles.chip}>
                {recipient.name ?? recipient.address}
                <button
                  type="button"
                  className={styles.chipRemove}
                  aria-label={`Remove ${recipient.address}`}
                  onClick={() => patch({ cc: draft.cc.filter((_, i) => i !== index) })}
                >
                  <svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true">
                    <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            ))}
            <input
              className={styles.recipientInput}
              value={ccInput}
              aria-label="Cc"
              autoComplete="off"
              onChange={(event) => setCcInput(event.target.value)}
              onKeyDown={(event: ReactKeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter' || event.key === ',' || event.key === 'Tab') {
                  const parsed = parseRecipient(ccInput);
                  if (parsed) {
                    event.preventDefault();
                    patch({ cc: [...draft.cc, parsed] });
                    setCcInput('');
                  }
                }
              }}
            />
          </div>
        </div>
      ) : null}

      {draft.mode === 'new' || draft.mode === 'forward' ? (
        <div className={styles.subjectRow}>
          <input
            className={styles.subjectInput}
            value={draft.subject}
            aria-label="Subject"
            placeholder="Subject"
            autoComplete="off"
            onChange={(event) => patch({ subject: event.target.value })}
          />
        </div>
      ) : null}

      {aiOpen && aiEnabled ? (
        <div className={styles.aiBar}>
          <input
            className={styles.aiInput}
            value={instruction}
            autoFocus
            aria-label="Tell the assistant what to write"
            placeholder="Tell the assistant what to write…"
            onChange={(event) => setInstruction(event.target.value)}
            onKeyDown={(event: ReactKeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void runAiDraft();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                setAiOpen(false);
              }
            }}
          />
          <button type="button" className={styles.aiRun} disabled={aiBusy} onClick={() => void runAiDraft()}>
            {aiBusy ? 'Drafting…' : 'Draft'}
          </button>
        </div>
      ) : null}

      <div className={styles.bodyWrap}>
        <div
          key={`${draft.key}:${bodyRevision}`}
          ref={editorRef}
          className={styles.editor}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label="Message body"
          dangerouslySetInnerHTML={{ __html: initialBodyRef.current }}
          onInput={readEditor}
          onKeyDown={(event) => {
            if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) retireAiChip();
          }}
        />

        {selectionToolbar && aiEnabled && draft.threadKey ? (
          <div className={styles.selectionToolbar} style={{ top: selectionToolbar.top, left: selectionToolbar.left }}>
            {SELECTION_ACTIONS.map(({ label, directive }) => (
              <button
                key={label}
                type="button"
                className={styles.selectionAction}
                disabled={aiBusy}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => void transformSelection(label, directive)}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {attachments.length > 0 ? (
        <div className={styles.attachments}>
          {attachments.map((file, i) => (
            <span key={`${file.filename}-${i}`} className={styles.attachment}>
              <span className={styles.attachmentName} title={file.filename}>
                {file.filename}
              </span>
              <span className={styles.attachmentSize}>{formatBytes(file.size)}</span>
              <button
                type="button"
                className={styles.attachmentRemove}
                onClick={() => removeAttachment(i)}
                aria-label={`Remove ${file.filename}`}
              >
                ×
              </button>
            </span>
          ))}
          <span className={styles.attachmentTotal}>
            {formatBytes(attachedBytes)} of {MAX_ATTACHMENT_BYTES / 1024 / 1024} MB
          </span>
        </div>
      ) : null}

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.send}
          onClick={() => queueSend({})}
          title={`Send (${isMac ? '⌘' : 'Ctrl+'}⏎)`}
        >
          Send
          <kbd className={styles.sendKey}>{isMac ? '⌘⏎' : 'Ctrl⏎'}</kbd>
        </button>

        <div className={styles.footerActions}>
          <button
            type="button"
            className={styles.footerButton}
            onClick={() => fileInputRef.current?.click()}
            disabled={attaching}
            title={`Attach files (${isMac ? '⌘⇧A' : 'Ctrl+Shift+A'})`}
          >
            {attaching ? 'Reading…' : 'Attach'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className={styles.fileInput}
            onChange={(e) => {
              if (e.target.files) void addFiles(e.target.files);
            }}
          />
          <button
            type="button"
            className={styles.footerButton}
            onClick={() => setLaterOpen((value) => !value)}
            aria-expanded={laterOpen}
          >
            Send later
          </button>
          {aiEnabled && draft.threadKey ? (
            <button type="button" className={styles.footerButton} onClick={() => setAiOpen((value) => !value)}>
              Assistant
            </button>
          ) : null}
          <button type="button" className={styles.footerButton} onClick={discard}>
            Discard
          </button>
        </div>

        <div className={styles.footerStatus}>
          {aiDrafted && !aiChipRetired.current ? <span className={styles.aiChip}>AI-drafted</span> : null}
          {error ? (
            <span className={styles.error}>{error}</span>
          ) : savedAt !== null ? (
            <span className={styles.saved}>Saved</span>
          ) : null}
        </div>

        {laterOpen ? (
          <div className={styles.laterMenu} role="menu">
            {sendLaterPresets().map(({ label, delayMs }) => (
              <button
                key={label}
                type="button"
                role="menuitem"
                className={styles.laterItem}
                onClick={() => {
                  setLaterOpen(false);
                  queueSend({ delayMs, label: `Scheduled — ${label.toLowerCase()}` });
                }}
              >
                {label}
              </button>
            ))}
            <p className={styles.laterNote}>Kiln sends scheduled mail from this browser, so keep it open.</p>
          </div>
        ) : null}
      </div>
    </div>
  );

  // Docked into the reading column when the shell offers one; otherwise it
  // behaves like the popout window, which is the only sane fallback on a
  // layout that has no reading column at all.
  return createPortal(body, dock ?? document.body);
}

/**
 * The shell renders the composer as a sibling of the panes, so it has to find
 * the reading column itself and add its own flex child there. A `data-kiln-dock`
 * hook wins; the aria-label is the documented fallback.
 */
function useDockTarget(active: boolean): HTMLElement | null {
  const [node, setNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!active || typeof document === 'undefined') {
      setNode(null);
      return;
    }
    const pane = document.querySelector<HTMLElement>(
      '[data-kiln-dock="composer"], section[aria-label="Message"]',
    );
    if (!pane) {
      setNode(null);
      return;
    }
    const host = document.createElement('div');
    host.dataset.kilnComposerDock = 'true';
    host.style.flex = '0 0 auto';
    host.style.display = 'flex';
    host.style.flexDirection = 'column';
    host.style.minHeight = '0';
    // "At most 60% of the pane height", enforced by the host so the composer
    // never has to know how tall the reading column is.
    host.style.maxHeight = '60%';
    pane.appendChild(host);
    setNode(host);
    return () => {
      host.remove();
      setNode(null);
    };
  }, [active]);

  return node;
}

/** The account hue travels into CSS as a custom property. */
function railStyle(color: string, transform?: string): CSSProperties {
  return { '--rail': color, transform } as CSSProperties;
}

const SELECTION_ACTIONS: { label: string; directive: string }[] = [
  { label: 'Shorten', directive: 'Make this passage shorter while keeping its meaning.' },
  { label: 'Rephrase', directive: 'Rephrase this passage in different words.' },
  { label: 'More formal', directive: 'Rewrite this passage in a more formal register.' },
  { label: 'Fix grammar', directive: 'Correct grammar and spelling in this passage, changing nothing else.' },
];

function sendLaterPresets(): { label: string; delayMs: number }[] {
  return [
    { label: 'In 1 hour', delayMs: 60 * 60 * 1000 },
    { label: 'In 3 hours', delayMs: 3 * 60 * 60 * 1000 },
    { label: 'Tomorrow morning', delayMs: millisUntilTomorrowMorning() },
  ];
}

function millisUntilTomorrowMorning(): number {
  const target = new Date();
  target.setDate(target.getDate() + 1);
  target.setHours(8, 0, 0, 0);
  return Math.max(60_000, target.getTime() - Date.now());
}

/* ------------------------------ store binding ----------------------------- */

const LAST_ACCOUNT_KEY = 'kiln.last-account';

function readLastAccountId(): number | null {
  try {
    const raw = localStorage.getItem(LAST_ACCOUNT_KEY);
    if (raw === null) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * The store's draft has no "popout" field, so the popout shortcuts leave a flag
 * here for the next composer that opens to pick up.
 */
let popoutRequested = false;

export function requestPopoutCompose(): void {
  popoutRequested = true;
}

function consumePopoutRequest(): boolean {
  const value = popoutRequested;
  popoutRequested = false;
  return value;
}

/** An empty field in the store means "derive it"; a filled one is deliberate. */
export function storeDraftToIntent(draft: ComposerState, popout: boolean): ComposerIntent {
  return {
    mode: draft.mode,
    popout,
    threadKey: draft.threadKey,
    accountId: draft.accountId,
    to: draft.to.length > 0 ? draft.to : undefined,
    cc: draft.cc.length > 0 ? draft.cc : undefined,
    bcc: draft.bcc.length > 0 ? draft.bcc : undefined,
    subject: draft.subject === '' ? undefined : draft.subject,
    bodyHtml: draft.bodyHtml === '' ? undefined : draft.bodyHtml,
    inReplyTo: draft.inReplyTo,
    references: draft.references,
  };
}

export function intentToStoreDraft(intent: ComposerIntent): Partial<ComposerDraft> {
  return {
    mode: intent.mode,
    accountId: intent.accountId ?? null,
    to: intent.to ?? [],
    cc: intent.cc ?? [],
    bcc: intent.bcc ?? [],
    subject: intent.subject ?? '',
    bodyHtml: intent.bodyHtml ?? '',
    inReplyTo: intent.inReplyTo ?? null,
    references: intent.references ?? null,
    threadKey: intent.threadKey ?? null,
  };
}

/**
 * The shell mounts this with no props: everything it needs is in the UI store
 * and the query cache. Props remain available for tests and previews.
 */
export function Composer(overrides: Partial<ComposerProps> = {}): ReactElement {
  const composer = useUiStore((s) => s.composer);
  const openComposer = useUiStore((s) => s.openComposer);
  const closeComposer = useUiStore((s) => s.closeComposer);
  const selectedThreadKey = useUiStore((s) => s.selectedThreadKey);

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => api.accounts(), staleTime: 60_000 });
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 5 * 60_000 });

  const threadKey = (composer.open ? composer.threadKey : null) ?? selectedThreadKey;
  const threadQuery = useQuery({
    queryKey: ['thread', threadKey],
    queryFn: () => api.thread(threadKey as string),
    enabled: threadKey !== null,
  });

  const [lastAccountId, setLastAccountId] = useState<number | null>(readLastAccountId);

  // Deriving during render, not in an effect: the popout flag has to be part of
  // the very first intent or the window would open docked and then jump.
  const seenComposer = useRef<ComposerState | null>(null);
  const popoutRef = useRef(false);
  if (composer.open && seenComposer.current !== composer) {
    seenComposer.current = composer;
    popoutRef.current = consumePopoutRequest();
  }
  if (!composer.open) seenComposer.current = null;

  const intent = useMemo<ComposerIntent | null>(
    () => (composer.open ? storeDraftToIntent(composer, popoutRef.current) : null),
    [composer],
  );

  return (
    <>
      <ComposerView
        accounts={accountsQuery.data?.accounts ?? []}
        aiEnabled={meQuery.data?.aiEnabled === true}
        intent={intent}
        thread={threadQuery.data?.thread ?? null}
        messages={threadQuery.data?.messages ?? []}
        lastUsedAccountId={lastAccountId}
        onOpen={(next) => {
          if (next.popout) requestPopoutCompose();
          openComposer(intentToStoreDraft(next));
        }}
        onClose={closeComposer}
        onAccountUsed={(id) => {
          setLastAccountId(id);
          try {
            localStorage.setItem(LAST_ACCOUNT_KEY, String(id));
          } catch {
            // The preference simply will not persist.
          }
        }}
        {...overrides}
      />
      <ToastOutlet />
    </>
  );
}

export default Composer;
